import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/outline';
import useAuthStore from '../../store/authStore';
import { getContractsForUser, getPaymentsForContract } from '../../services/contractService';
import { Payment } from '../../services/contractService';
import { ClockIcon, CheckCircleIcon } from '@heroicons/react/solid';

// Define valid payment statuses
type PaymentStatus = 'مدفوع' | 'مستحق' | 'متأخر';

// Extended interface with required contractId
interface PaymentWithDetails extends Omit<Payment, 'status'> {
  contractId: string;
  contractTitle: string;
  formattedDate: string;
  formattedAmount: string;
  status: PaymentStatus;
}

const PaymentsPage: React.FC = () => {
  // All hooks must be called unconditionally at the top level
  const navigate = useNavigate();
  const { user, userData, isLoading: isAuthLoading } = useAuthStore();
  const [isInitialized, setIsInitialized] = useState(false);
  const [duePayments, setDuePayments] = useState<PaymentWithDetails[]>([]);
  const [pastPayments, setPastPayments] = useState<PaymentWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const userWithRole = user as { uid: string; role?: 'lawyer' | 'client' } | null;
  const isClient = userData?.role === 'client';
  const canFetchPayments = Boolean(userWithRole?.uid && userData?.role === 'client' && isInitialized);

  // Handle authentication and initialization
  useEffect(() => {
    if (isAuthLoading) return; // Still loading auth state
    
    if (!isClient) {
      navigate('/unauthorized', { replace: true });
    } else if (isClient && !isInitialized) {
      setIsInitialized(true);
    }
  }, [isAuthLoading, isClient, isInitialized, navigate]);
  
  const handlePayNow = (contractId: string, paymentId: string) => {
    navigate(`/contracts/${contractId}/pay/${paymentId}`);
  };

  // Fetch payments when user data is ready
  useEffect(() => {
    if (!canFetchPayments || !userWithRole?.uid || !userData?.role) return;

    const fetchPayments = async () => {
      console.log('Starting payments fetch with user ID:', userWithRole.uid);
      
      try {
        setIsLoading(true);
        setError(null);
        
        // Step 1: Get user's contracts
        const contracts = await getContractsForUser(userWithRole.uid, userData.role as 'client' | 'lawyer');
        
        if (contracts.length === 0) {
          console.log('No contracts found for user');
          setDuePayments([]);
          setPastPayments([]);
          return;
        }
        
        // Step 2: Get payments for each contract
        const allPaymentsPromises = contracts.map(contract => 
          getPaymentsForContract(contract.id!)
            .then(payments => payments.map(payment => ({
              ...payment,
              contractId: contract.id!,
              contractTitle: contract.title || 'عقد بدون عنوان',
              formattedDate: payment.dueDate.toLocaleDateString('en-SA', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                calendar: 'islamic-umalqura',
                numberingSystem: 'arab'
              }),
              formattedAmount: payment.amount.toLocaleString('en-SA')
            })))
        );

        // Process all payments
        const allPayments = (await Promise.all(allPaymentsPromises)).flat();
        
        // Split into due and past payments
        const due: PaymentWithDetails[] = [];
        const past: PaymentWithDetails[] = [];
        
        allPayments.forEach(payment => {
          // Normalize payment status to Arabic
          let normalizedStatus: PaymentStatus;
          const status = payment.status as string; // Type assertion to handle both string and custom types
          
          if (status === 'paid' || status === 'مدفوع') {
            normalizedStatus = 'مدفوع';
            past.push({
              ...payment,
              status: normalizedStatus
            });
          } else {
            // For any non-paid status, use 'مستحق' (due) or 'متأخر' (overdue) based on due date
            const now = new Date();
            const dueDate = payment.dueDate instanceof Date ? payment.dueDate : new Date(payment.dueDate);
            normalizedStatus = dueDate < now ? 'متأخر' : 'مستحق';
            
            due.push({
              ...payment,
              status: normalizedStatus
            });
          }
        });
        
        // Sort by date (newest first)
        const sortByDate = (a: PaymentWithDetails, b: PaymentWithDetails) => 
          new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime();
        
        setDuePayments(due.sort(sortByDate));
        setPastPayments(past.sort(sortByDate));
        
      } catch (err) {
        console.error('Error in fetchPayments:', err);
        setError('حدث خطأ أثناء جلب بيانات الدفعات');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPayments();
  }, [canFetchPayments, userWithRole?.uid, userData?.role]);
  
  // Show loading state while auth is being checked or initializing
  if (isAuthLoading || !isInitialized || (isClient && isLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // If not a client, don't render anything (will be redirected by the effect)
  if (!isClient) {
    return null;
  }

  const renderPaymentTable = (title: string, payments: PaymentWithDetails[], showPayNow = false) => (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6 font-tajawal">
      <div className="p-4 border-b border-gray-50">
        <h2 className="font-semibold text-gray-800 text-lg">{title}</h2>
      </div>
      {payments.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <p>لا توجد دفعات لعرضها</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {payments.map((payment) => (
            <div key={payment.id} className="p-4 flex items-center">
              <div className="ml-4">
                {payment.status === 'مدفوع' ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                ) : (
                  <ClockIcon className="h-6 w-6 text-amber-500" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-gray-900">{payment.contractTitle}</h3>
                    <p className="text-sm text-gray-500 mt-1">{payment.formattedDate}</p>
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-lg text-gray-900 tracking-wider">
                      {payment.formattedAmount}
                      <span className="text-sm font-normal text-gray-500 mr-1">ر.س</span>
                    </p>
                    {showPayNow && payment.status !== 'مدفوع' && (
                      <button 
                        onClick={() => handlePayNow(payment.contractId, payment.id!)}
                        style={{
                          marginTop: '0.25rem',
                          fontSize: '0.75rem',
                          backgroundColor: 'oklch(0.55 0.24 262.91)',
                          color: '#ffffff',
                          padding: '0.375rem 0.75rem',
                          borderRadius: '9999px',
                          fontWeight: 600,
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          outline: 'none',
                          fontFamily: 'inherit',
                          lineHeight: '1.25'
                        }}
                        onMouseOver={e => e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)'}
                        onMouseOut={e => e.currentTarget.style.backgroundColor = 'oklch(0.55 0.24 262.91)'}
                        onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)'}
                        onBlur={e => e.currentTarget.style.boxShadow = 'none'}
                      >
                        سدد الآن
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Show error message if there's an error
  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 p-4 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  // Only show first 3 items in each section
  const visibleDuePayments = duePayments.slice(0, 3);
  const visiblePastPayments = pastPayments.slice(0, 3);
  const hasMoreDuePayments = duePayments.length > 3;
  const hasMorePastPayments = pastPayments.length > 3;

  return (
    <div className="p-4 bg-gray-50 min-h-screen font-tajawal">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 rounded-full hover:bg-gray-200"
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-700 rotate-180" />
          </button>
          <h2 className="text-2xl font-bold text-gray-900 mr-4">الدفعات</h2>
        </div>
      </div>

      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">الدفعات المستحقة</h3>
          {hasMoreDuePayments && (
            <button 
              onClick={() => navigate('/payments?filter=due')}
              className="text-blue-600 text-sm font-medium"
            >
              عرض الكل
            </button>
          )}
        </div>
        {visibleDuePayments.length > 0 ? (
          <div className="space-y-3">
            {visibleDuePayments.map(payment => (
              <div key={payment.id} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-gray-900">{payment.contractTitle}</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      {payment.formattedDate}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-gray-900">
                      {payment.formattedAmount}
                      <span className="text-sm font-normal text-gray-500 mr-1">ر.س</span>
                    </p>
                    {payment.status !== 'مدفوع' && (
                      <button
                        onClick={() => handlePayNow(payment.contractId, payment.id!)}
                        className="mt-2 text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-medium w-full"
                      >
                        سدد الآن
                      </button>
                    )}
                    {payment.status === 'مدفوع' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 bg-green-100 text-green-800">
                        مدفوع
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            لا توجد دفعات مستحقة حالياً
          </div>
        )}
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">سجل الدفعات</h3>
          {hasMorePastPayments && (
            <button 
              onClick={() => navigate('/payments?filter=past')}
              className="text-blue-600 text-sm font-medium"
            >
              عرض الكل
            </button>
          )}
        </div>
        {visiblePastPayments.length > 0 ? (
          <div className="space-y-3">
            {visiblePastPayments.map(payment => (
              <div key={payment.id} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-gray-900">{payment.contractTitle}</h4>
                    <p className="text-sm text-gray-500 mt-1">
                      {payment.formattedDate}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-gray-900">
                      {payment.formattedAmount}
                      <span className="text-sm font-normal text-gray-500 mr-1">ر.س</span>
                    </p>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 bg-green-100 text-green-800">
                      مدفوع
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            لا توجد مدفوعات سابقة
          </div>
        )}
      </div>
    </div>
  );
};

// New PaymentCard component for better code organization
const PaymentCard: React.FC<{ 
  payment: PaymentWithDetails; 
  onPayNow?: () => void;
  isPaid?: boolean;
}> = ({ payment, onPayNow, isPaid = false }) => (
  <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm">
    <div className="flex justify-between items-start">
      <div>
        <h4 className="font-semibold text-gray-900">{payment.contractTitle}</h4>
        <p className="text-sm text-gray-500 mt-1">
          {payment.formattedDate}
        </p>
      </div>
      <div className="text-right">
        <p className="font-bold text-lg text-gray-900">
          {payment.formattedAmount}
          <span className="text-sm font-normal text-gray-500 mr-1">ر.س</span>
        </p>
        {onPayNow && !isPaid && (
          <button
            onClick={onPayNow}
            className="mt-2 text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-medium w-full"
          >
            سدد الآن
          </button>
        )}
        {isPaid && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 bg-green-100 text-green-800">
            مدفوع
          </span>
        )}
      </div>
    </div>
  </div>
);

export default PaymentsPage;
